import React from "react";
import { AmlStatusEnum } from "../model";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useBoolean from "../hooks/useBoolean";
import { useFormik } from "formik";
import * as yup from "yup";
import createAmlStatusMachine, {
  AmlStateMachine,
  AmlStateMachineConfig,
  createAmlStatusMachineConfig,
} from "./AmlStatusMachine";
import { AmlStatus, OnboardingModel, StatusNaming } from "../constant/amlConstant";
import Text from "../components/Text";
import useConfirmDialog from "../hooks/useConfirmDialog";
import { StatusUpdatePayload } from "../api/transformers/aml";
import useStatusEditValidation from "../hooks/useStatusEditValidation";
import { AmlAPI } from "../api";
import useMessage from "../hooks/useMessage";
import useAssociatedPartyStore from "../zstore/associatedPartyStore";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import { useShallow } from "zustand/react/shallow";
import { useSelector } from "react-redux";
import { selectFromAmlStore } from "../amlSlice";
import { getEntityTypeCheckers } from "../utils/investor";
import { scrollTo } from "../utils/dom";

type AmlStatusEditModalProps = {
  personId: string;
  open: boolean;
  status?: AmlStatusEnum;
  onboardingModel?: string;
  onCancel: () => void;
  onSuccess: () => void;
};

type FormikValue = {
  status: string;
  comments: string;
};

const validationSchema = yup.object({
  status: yup.string().required("This Field is Required."),
  comments: yup.string().required("This Field is Required."),
});

const AmlStatusEditModal: React.FC<AmlStatusEditModalProps> = ({
  open,
  personId,
  status,
  onboardingModel,
  onCancel,
  onSuccess,
}) => {
  const [submitting, { setTrue: startSubmitting, setFalse: finishSubmitting }] =
    useBoolean();
  const machineRef = React.useRef<AmlStateMachine | null>(null);
  const { confirm: showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const message = useMessage();
  const { validate } = useStatusEditValidation();
  const { associatedParties } = useAssociatedPartyStore(
    useShallow((state) => ({ associatedParties: state.data }))
  );
  const { amlDetail } = useInvestorAmlDetailStore(
    useShallow((state) => ({ amlDetail: state.data }))
  );
  const entityTypes = useSelector(selectFromAmlStore((state) => state.entityTypes));
  const entityTypeCheckers = getEntityTypeCheckers(entityTypes);
  const formik = useFormik<FormikValue>({
    initialValues: {
      status: "",
      comments: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!checkPreconditions(values)) {
        onCancel();
        return;
      }
      const event = {
        type: `TO${values.status as AmlStatusEnum}`,
        data: buildPayload(values),
      } as const;
      machineRef.current && (await machineRef.current.transition(event));
    },
  });
  const [options, setOptions] = React.useState<StatusOption[]>([]);
  const submitStatusChange = (_: unknown, event: any) => {
    const { data } = event as { data: StatusUpdatePayload };
    if (onboardingModel === OnboardingModel.NONE_RELIANCE && (data.status === AmlStatus.ACT || data.status === AmlStatus.REJ)) {
      showConfirmDialog({
        title: "Confirm Notification",
        content: "Send a notification to advise the outcome?",
        cancelText: "No",
        okText: "Yes",
        onOk: () => {
          startSubmitting();
          data.sendNotification = true;
          return updateStatus(data);
        },
        onCancel() {
          startSubmitting();
          data.sendNotification = false;
          return updateStatus(data);
        },
      });
    } else {
      startSubmitting();
      updateStatus(data);
    }
  };
  React.useEffect(() => {
    if (!status) {
      return;
    }
    const config = createAmlStatusMachineConfig(status);
    // Add tooltip (keep selectable) for Verified if parties non-compliant
    const isCorporateOrTrust =
      amlDetail && (entityTypeCheckers.isCorporate(amlDetail.entityType) || entityTypeCheckers.isTrust(amlDetail.entityType));
    if (isCorporateOrTrust) {
      const transitions = config.states[status].on;
      Object.keys(transitions).forEach((evt) => {
        const t = (transitions as any)[evt];
        if (t?.target === AmlStatus.ACT) {
          const isPartyCompliant = associatedParties.every(
            (p: any) => p.amlStatus === AmlStatus.ACT || p.amlStatus === AmlStatus.NRQ
          );
          (transitions as any)[evt] = {
            ...t,
            metaData: {
              ...(t.metaData || {}),
              tooltip: !isPartyCompliant
                ? "All Associated Parties must be Verified or Not Required before the entity can be verified."
                : StatusNaming[AmlStatus.ACT],
            },
          };
        }
      });
    }
    config.actions = {
      submitStatusChange,
    };
    const options = getOptions(config, status);
    machineRef.current = createAmlStatusMachine(config);
    setOptions(options);
  }, [status, associatedParties, amlDetail, entityTypes]);

  // Only change the selection; warnings happen on OK submit
  const handleStatusChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    formik.handleChange(e);
  };

  const updateStatus = (payload: StatusUpdatePayload) => {
    return AmlAPI.updateStatus(personId, payload)
      .then(() => {
        onSuccess();
        formik.resetForm();
        message.success("Successfully Submitted.");
      })
      .catch((error) => {
        console.error(error);
        message.error("Error! We couldn't send the details for approval.");
      })
      .finally(() => {
        finishSubmitting();
      });
  };

  // Reset fields when modal closes
  React.useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  // Strengthen preconditions: block ACT when parties non-compliant (Trust/Corp)
  function checkPreconditions(values: FormikValue) {
    if (values.status === AmlStatus.ACT) {
      const okBaseValidation = validate();
      if (!okBaseValidation) return false;
      const isCorporateOrTrust =
        amlDetail && (entityTypeCheckers.isCorporate(amlDetail.entityType) || entityTypeCheckers.isTrust(amlDetail.entityType));
      if (isCorporateOrTrust) {
        const isPartyCompliant = associatedParties.every(
          (p: any) => p.amlStatus === AmlStatus.ACT || p.amlStatus === AmlStatus.NRQ
        );
        if (!isPartyCompliant) {
          message.warning({ content: "All Associated Parties must be Verified or Not Required before the entity can be verified.", style: { backgroundColor: "red" } });
          scrollTo("associated-parties-section", { block: "center" });
          return false;
        }
      }
    }
    return true;
  }

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        PaperProps={{
          sx: {
            p: 0,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "1.2rem" }}>Edit Status</DialogTitle>
        <DialogContent>
          <Stack
            direction="column"
            rowGap="12px"
            sx={{ width: "100%", marginTop: "10px" }}
          >
            <Grid container>
              <Grid item xs={2}>
                Current:
              </Grid>
              {status && (
                <Grid item xs={9}>
                  {StatusNaming[status]}
                </Grid>
              )}
            </Grid>
            <TextField
              label="New Status *"
              name="status"
              size="small"
              select
              value={formik.values?.status}
              onChange={handleStatusChange}
              error={formik.touched.status && Boolean(formik.errors.status)}
              helperText={formik.touched.status && formik.errors.status}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      p: 0,
                    },
                  },
                },
              }}
            >
              {options.map((el) => (
                <MenuItem
                  key={el.value} // <-- add unique key
                  value={el.value}
                  disabled={el.disabled}
                >
                  <Tooltip title={el.title}>
                    <Text>{el.label}</Text>
                  </Tooltip>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Note"
              name="comments"
              value={formik.values?.comments}
              multiline
              rows={3}
              size="small"
              inputProps={{ maxLength: 200 }}
              placeholder="add your note here"
              disabled={submitting}
              onChange={formik.handleChange}
              error={formik.touched.comments && Boolean(formik.errors.comments)}
              helperText={formik.touched.comments && formik.errors.comments}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" columnGap="8px">
            <Button
              variant="text"
              sx={{
                backgroundColor: "transparent",
                color: "primary.main",
                padding: "6px 8px",
                minWidth: "64px",
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: 1.75,
                borderRadius: "4px",
                textTransform: "uppercase",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  boxShadow: "none",
                },
              }}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              sx={{ borderRadius: "4px" }}
              onClick={formik.submitForm}
              loading={submitting}
            >
              OK
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Dialog>
      {ConfirmDialog}
    </>
  );
};

type StatusOption = {
  value: string;
  label: string;
  disabled: boolean;
  title: string;
};

function getOptions(
  config: AmlStateMachineConfig,
  status: AmlStatusEnum
): StatusOption[] {
  const transitions = config.states[status].on;
  const options = Object.entries(transitions).map(([key, obj]) => {
    return {
      value: obj.target,
      label: StatusNaming[obj.target],
      disabled: obj.metaData?.disabled,
      title: obj.metaData?.disabled
        ? obj.metaData?.tooltip
        : StatusNaming[obj.target],
    };
  });
  return options;
}

function buildPayload(values: FormikValue): StatusUpdatePayload {
  return {
    status: values.status,
    comment: values.comments,
    sendNotification: false,
  };
}

export default AmlStatusEditModal;
